import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FaArrowLeft,
  FaCreditCard,
  FaExclamationTriangle,
  FaExchangeAlt,
  FaFileInvoiceDollar,
  FaMapMarkerAlt,
  FaRegCalendarAlt,
  FaSearch,
  FaCheck,
  FaInfoCircle
} from 'react-icons/fa';
import { Modal, DatePicker } from 'antd';
import dayjs from 'dayjs';
import StatusToast from '../../components/StatusToast/StatusToast';
import { getClubPaymentInfo, transferSession } from '../../services/clubApplicationService';
import api from '../../services/api';
import './ClubPaymentPage.scss';

const daysOfWeek = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];

const ClubPaymentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [taxNumber, setTaxNumber] = useState(location.state?.taxNumber || '');
  const [selectedClubApplicationId, setSelectedClubApplicationId] = useState(location.state?.clubApplicationId || null);
  const [loading, setLoading] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  // Transfer modal state
  const [transferModalVisible, setTransferModalVisible] = useState(false);
  const [transferData, setTransferData] = useState(null); // { assignmentId, sessionDate, dayOfWeek, startTime, endTime }
  const [transferDate, setTransferDate] = useState(null);
  const [transferStartTime, setTransferStartTime] = useState('');
  const [transferEndTime, setTransferEndTime] = useState('');
  const [transferNotes, setTransferNotes] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);

  // Available time slots for the chosen transfer date
  const timeSlotOptions = [];
  for (let h = 8; h < 23; h++) {
    const start = `${String(h).padStart(2,'0')}:00`;
    const end = `${String(h+1).padStart(2,'0')}:00`;
    timeSlotOptions.push({ start, end, label: `${start} - ${end}` });
  }

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  const showToast = (type, message, duration = 4000) => {
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }

    setToast({ type, message });
    toastTimerRef.current = setTimeout(() => setToast(null), duration);
  };

  const applyPaymentInfo = (data, preferredClubApplicationId = null) => {
    setPaymentInfo(data);
    const regionBreakdowns = data?.regionBreakdowns || [];
    const preferredId = preferredClubApplicationId
      || (regionBreakdowns.some((region) => region.clubApplicationId === selectedClubApplicationId)
        ? selectedClubApplicationId
        : null)
      || regionBreakdowns.find((region) => !region.isExpired)?.clubApplicationId
      || regionBreakdowns[0]?.clubApplicationId
      || null;
    setSelectedClubApplicationId(preferredId);
  };

  const fetchPaymentInfo = async (taxNo, options = {}) => {
    const trimmedTaxNo = taxNo?.trim();

    if (!trimmedTaxNo) {
      setError('Lütfen bir vergi no veya TCKN girin.');
      return;
    }

    setLoading(true);
    setError('');
    setPaymentInfo(null);

    try {
      const response = await getClubPaymentInfo(trimmedTaxNo, options);
      if (response?.isSuccess !== false && response?.data) {
        applyPaymentInfo(response.data, options.clubApplicationId || null);
      } else {
        setError(
          response?.resultMessage || 'Bu vergi numarasına ait onaylı kulüp ödeme bilgisi bulunamadı.'
        );
        setSelectedClubApplicationId(null);
      }
    } catch (err) {
      console.error('Ödeme bilgisi sorgulanırken hata:', err);
      showToast('error', 'Ödeme bilgisi sorgulanırken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (location.state?.taxNumber) {
      fetchPaymentInfo(location.state.taxNumber, {
        clubApplicationId: location.state?.clubApplicationId,
        facilityId: location.state?.facilityId
      });
    }
  }, [location.state?.taxNumber, location.state?.clubApplicationId, location.state?.facilityId]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchPaymentInfo(taxNumber);
  };

  const handlePayment = async () => {
    if (!selectedRegion?.clubApplicationId) {
      return;
    }

    Modal.confirm({
      title: 'Ödeme Onayı',
      content: `${paymentInfo.clubName} için ${selectedRegion.facilityName} tesisine ait ${selectedTotalAmount} TL ödeme alınacaktır. Onaylıyor musunuz?`,
      okText: 'Evet',
      cancelText: 'Hayır',
      onOk: async () => {
        setLoading(true);
        try {
          const response = await api.post('/api/sports/club-applications/mark-paid', {
            clubApplicationIds: [selectedRegion.clubApplicationId],
            amount: selectedTotalAmount
          });

          if (response?.isSuccess !== false) {
            showToast('success', 'Ödeme başarıyla alındı ve faturalandırıldı.');

            const refresh = await getClubPaymentInfo(taxNumber, {
              clubApplicationId: selectedRegion.clubApplicationId,
              facilityId: selectedRegion.facilityId
            });
            if (refresh?.isSuccess !== false && refresh?.data) {
              applyPaymentInfo(refresh.data, selectedRegion.clubApplicationId);
              setError('');
            }
          } else {
            showToast('error', response?.resultMessage || 'Ödeme işlemi tamamlanamadı.');
          }
        } catch (err) {
          console.error('Ödeme işlemi sırasında hata:', err);
          showToast('error', 'Ödeme işlemi sırasında hata oluştu.');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  // ── Transfer Mantığı ──

  const openTransferModal = (slot, session) => {
    setTransferData({
      assignmentId: slot.assignmentId,
      sessionDate: session.sessionDate,
      dayOfWeek: slot.dayOfWeek,
      startTime: slot.startTime,
      endTime: slot.endTime
    });
    setTransferDate(null);
    setTransferStartTime('');
    setTransferEndTime('');
    setTransferNotes('');
    setTransferModalVisible(true);
  };

  const handleTransfer = async () => {
    if (!transferData || !transferDate) {
      showToast('error', 'Lütfen bir hedef tarih seçin.');
      return;
    }

    if (!transferStartTime || !transferEndTime) {
      showToast('error', 'Lütfen bir hedef saat dilimi seçin.');
      return;
    }

    setTransferLoading(true);
    try {
      const payload = {
        assignmentId: transferData.assignmentId,
        originalDate: new Date(transferData.sessionDate).toISOString(),
        newDate: transferDate.toISOString(),
        newStartTime: transferStartTime,
        newEndTime: transferEndTime,
        notes: transferNotes || null
      };

      const response = await transferSession(payload);

      if (response?.isSuccess !== false) {
        showToast('success', 'Seans başarıyla aktarıldı!');
        setTransferModalVisible(false);

        // Refresh data
        const refresh = await getClubPaymentInfo(taxNumber, {
          clubApplicationId: selectedRegion?.clubApplicationId || selectedClubApplicationId,
          facilityId: selectedRegion?.facilityId
        });
        if (refresh?.isSuccess !== false && refresh?.data) {
          applyPaymentInfo(
            refresh.data,
            selectedRegion?.clubApplicationId || selectedClubApplicationId || null
          );
        }
      } else {
        showToast('error', response?.resultMessage || 'Aktarım işlemi başarısız.');
      }
    } catch (err) {
      console.error('Aktarım hatası:', err);
      showToast('error', 'Aktarım sırasında hata oluştu.');
    } finally {
      setTransferLoading(false);
    }
  };

  // Check if a session date is in the past (kullanılmamış kabul edilir)
  const isSessionPast = (sessionDate) => {
    return new Date(sessionDate) < new Date();
  };

  const getDateKey = (value) => dayjs(value).format('YYYY-MM-DD');

  const isExtraSessionPaid = (slot, extraSession) => {
    if (extraSession?.isPaid || extraSession?.IsPaid || slot?.isPaid) {
      return true;
    }
    const sessionDate = extraSession?.sessionDate || extraSession?.SessionDate;
    const paidUntil = slot?.paidUntilDate ? dayjs(slot.paidUntilDate).startOf('day') : null;
    if (paidUntil && dayjs(sessionDate).startOf('day').valueOf() <= paidUntil.valueOf()) {
      return true;
    }

    const extraDateKey = getDateKey(sessionDate);
    return (slot?.sessionPayments || []).some((payment) =>
      getDateKey(payment.sessionDate || payment.SessionDate) === extraDateKey
      && !(payment.isTransferred || payment.IsTransferred)
    );
  };

  // Choose the session status badge
  const getSessionBadge = (session) => {
    if (session.isTransferred) {
      return <span className="session-badge transferred"><FaExchangeAlt /> Aktarıldı</span>;
    }
    if (session.isPaid === false) {
      return <span className="session-badge unpaid"><FaExclamationTriangle /> Ödenmedi</span>;
    }
    return <span className="session-badge paid"><FaCheck /> Ödendi</span>;
  };

  const selectedRegion = paymentInfo?.regionBreakdowns?.find(
    (region) => region.clubApplicationId === selectedClubApplicationId
  ) || paymentInfo?.regionBreakdowns?.find((region) => !region.isExpired)
    || paymentInfo?.regionBreakdowns?.[0]
    || null;
  const visibleRegions = selectedRegion ? [selectedRegion] : [];
  const selectedFacilityDebts = selectedRegion
    ? (paymentInfo?.additionalDebts || []).filter(
      (debt) => Number(debt.facilityId) === Number(selectedRegion.facilityId)
    )
    : [];
  const selectedTotalAmount = (selectedRegion?.regionTotal || 0)
    + selectedFacilityDebts.reduce((sum, debt) => sum + (debt.amount || 0), 0);
  const summaryMembershipStartDate = selectedRegion?.membershipStartDate;
  const summaryMembershipEndDate = selectedRegion?.membershipEndDate;
  const isSelectedMembershipExpired = selectedRegion?.isExpired ?? false;

  return (
    <div className="club-payment-page">
      <StatusToast toast={toast} onClose={() => setToast(null)} />

      <div
        className="page-header"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div className="header-icon">
            <FaFileInvoiceDollar />
          </div>
          <div>
            <h2>Kulüp Ödeme Ekranı</h2>
            <p>Kulüp vergi no veya TCKN ile tüm bölgelere ait ödeme dökümünü listeleyin.</p>
          </div>
        </div>
        <button
          className="btn btn-outline-secondary"
          onClick={() => navigate(-1)}
          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <FaArrowLeft /> Geri Dön
        </button>
      </div>

      <div className="search-section card-box">
        <form onSubmit={handleSearch} className="search-form">
          <div className="form-group">
            <label>Vergi No / T.C. Kimlik No</label>
            <div className="input-with-icon">
              <input
                type="text"
                value={taxNumber}
                onChange={(e) => setTaxNumber(e.target.value)}
                placeholder="Örn: 1234567890"
              />
              <FaSearch className="icon" />
            </div>
          </div>
          <button type="submit" className="btn btn-primary search-btn" disabled={loading}>
            {loading ? 'Sorgulanıyor...' : 'Sorgula'}
          </button>
        </form>
        {error && (
          <div className="error-alert">
            <FaExclamationTriangle /> {error}
          </div>
        )}
      </div>

      {paymentInfo && (
        <div className="payment-details fade-in">
          <div className="club-summary card-box">
            <div className="summary-header">
              <h3>{paymentInfo.clubName}</h3>
              <span className={`status-badge ${isSelectedMembershipExpired ? 'expired' : 'active'}`}>
                {isSelectedMembershipExpired ? 'Üyelik Süresi Dolmuş' : 'Aktif Üye'}
              </span>
            </div>
            <div className="summary-info">
              <div className="info-item">
                <span className="label">Vergi No:</span>
                <span className="value">{paymentInfo.taxNumber}</span>
              </div>
              <div className="info-item">
                <span className="label">Üyelik Başlangıç:</span>
                <span className="value">
                  {summaryMembershipStartDate
                    ? new Date(summaryMembershipStartDate).toLocaleDateString('tr-TR')
                    : '-'}
                </span>
              </div>
              <div className="info-item">
                <span className="label">Üyelik Bitiş:</span>
                <span className="value">
                  {summaryMembershipEndDate
                    ? new Date(summaryMembershipEndDate).toLocaleDateString('tr-TR')
                    : '-'}
                </span>
              </div>
            </div>
          </div>

          {paymentInfo.regionBreakdowns && paymentInfo.regionBreakdowns.length > 1 && (
            <div className="card-box" style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                {paymentInfo.regionBreakdowns.map((region) => (
                  <button
                    key={region.clubApplicationId}
                    type="button"
                    className="btn"
                    onClick={() => setSelectedClubApplicationId(region.clubApplicationId)}
                    style={{
                      border: '1px solid',
                      borderColor: selectedClubApplicationId === region.clubApplicationId ? '#0d6efd' : '#d0d7de',
                      background: selectedClubApplicationId === region.clubApplicationId ? '#0d6efd' : '#fff',
                      color: selectedClubApplicationId === region.clubApplicationId ? '#fff' : '#334155'
                    }}
                  >
                    {region.facilityName}
                  </button>
                ))}
              </div>
            </div>
          )}

          {paymentInfo.regionBreakdowns && paymentInfo.regionBreakdowns.length > 0 ? (
            <div className="regions-container">
              {visibleRegions.map((region, index) => (
                <div key={index} className="region-card card-box">
                  <div className="region-header">
                    <div className="region-title">
                      <FaMapMarkerAlt className="region-icon" />
                      <h4>{region.regionName}</h4>
                    </div>
                    <div className="region-facility">{region.facilityName}</div>
                  </div>

                  <div className="table-responsive">
                    <table className="assignments-table">
                      <thead>
                        <tr>
                          <th>Gün</th>
                          <th>Saat</th>
                          <th>Hafta Sayısı</th>
                          <th>Birim Fiyat</th>
                          <th className="text-right">Tutar</th>
                        </tr>
                      </thead>
                      <tbody>
                        {region.assignments?.map((slot, idx) => (
                          <tr key={idx}>
                            <td>{daysOfWeek[slot.dayOfWeek]}</td>
                            <td>
                              {slot.startTime} - {slot.endTime}
                            </td>
                            <td>{slot.weekCount} Hafta</td>
                            <td>{slot.pricePerHour} TL</td>
                            <td className="text-right font-weight-bold">{slot.subTotal} TL</td>
                          </tr>
                        ))}
                        {(!region.assignments || region.assignments.length === 0) && (
                          <tr>
                            <td colSpan="5" className="text-center text-muted">
                              Bu bölgede atanmış aktif saat bulunmuyor.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Seans Ödeme Detayları ve Aktarma */}
                  {region.assignments?.some(s => (s.sessionPayments?.length > 0) || (s.extraSessions?.length > 0)) && (
                    <div className="session-payments-section">
                      <h5 style={{ margin: '16px 0 8px', fontSize: '14px', fontWeight: 600, color: '#475569' }}>
                        <FaInfoCircle style={{ marginRight: 6 }} /> Seans Detayları
                      </h5>
                      <table className="assignments-table session-table">
                        <thead>
                          <tr>
                            <th>Gün</th>
                            <th>Saat</th>
                            <th>Seans Tarihi</th>
                            <th>Durum</th>
                            <th>İşlem</th>
                          </tr>
                        </thead>
                        <tbody>
                          {region.assignments?.flatMap((slot) =>
                            (slot.sessionPayments || []).map((session, sIdx) => (
                              <tr key={`${slot.assignmentId}-${sIdx}`} className={session.isTransferred ? 'row-transferred' : ''}>
                                <td>{daysOfWeek[slot.dayOfWeek]}</td>
                                <td>{slot.startTime} - {slot.endTime}</td>
                                <td>{new Date(session.sessionDate).toLocaleDateString('tr-TR')}</td>
                                <td>{getSessionBadge(session)}</td>
                                <td>
                                  {!session.isTransferred && (
                                    <button
                                      className="btn btn-sm btn-transfer"
                                      onClick={() => openTransferModal(slot, session)}
                                      title="Bu seansı ileriye aktar"
                                    >
                                      <FaExchangeAlt /> Aktar
                                    </button>
                                  )}
                                  {session.isTransferred && (
                                    <span className="text-muted" style={{ fontSize: '12px' }}>—</span>
                                  )}
                                </td>
                              </tr>
                            ))
                          )}
                          {region.assignments?.flatMap((slot) =>
                            (slot.extraSessions || []).map((extraSession, eIdx) => {
                              const sessionDate = extraSession.sessionDate || extraSession.SessionDate;
                              const startTime = extraSession.startTime || extraSession.StartTime || slot.startTime;
                              const endTime = extraSession.endTime || extraSession.EndTime || slot.endTime;
                              const dayOfWeek = dayjs(sessionDate).day();
                              const session = {
                                ...extraSession,
                                sessionDate,
                                isPaid: isExtraSessionPaid(slot, extraSession)
                              };

                              return (
                                <tr key={`${slot.assignmentId}-extra-${extraSession.id || eIdx}`}>
                                  <td>
                                    {daysOfWeek[dayOfWeek]}
                                    <span className="session-badge" style={{ marginLeft: 6 }}>Ek Seans</span>
                                  </td>
                                  <td>{startTime} - {endTime}</td>
                                  <td>{new Date(sessionDate).toLocaleDateString('tr-TR')}</td>
                                  <td>{getSessionBadge(session)}</td>
                                  <td>
                                    <span className="text-muted" style={{ fontSize: '12px' }}>-</span>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="region-footer">
                    <span>Bölge Toplamı:</span>
                    <span className="region-total">{region.regionTotal} TL</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state card-box">
              <FaRegCalendarAlt />
              <p>Bu kulübe atanmış herhangi bir saat bulunamadı.</p>
            </div>
          )}

          {selectedRegion && (
            <div className="grand-total-card card-box">
              <div className="grand-total-info">
                <span>Genel Toplam Ödenecek Tutar</span>
                <h2>{selectedTotalAmount} TL</h2>
              </div>
              <button
                className="btn btn-success checkout-btn"
                onClick={handlePayment}
                disabled={loading}
              >
                <FaCreditCard /> Ödemeyi Tamamla
              </button>
            </div>
          )}
        </div>
      )}

      {/* Seans Aktarma Modalı */}
      <Modal
        title="Seans Aktarma"
        open={transferModalVisible}
        onCancel={() => setTransferModalVisible(false)}
        onOk={handleTransfer}
        okText="Aktarımı Onayla"
        cancelText="İptal"
        confirmLoading={transferLoading}
        okButtonProps={{ disabled: !transferDate || !transferStartTime || !transferEndTime }}
      >
        {transferData && (
          <div className="transfer-modal-content">
            <div className="transfer-info-box">
              <p><strong>Mevcut Seans:</strong></p>
              <p>Gün: <strong>{daysOfWeek[transferData.dayOfWeek]}</strong></p>
              <p>Saat: <strong>{transferData.startTime} - {transferData.endTime}</strong></p>
              <p>Tarih: <strong>{new Date(transferData.sessionDate).toLocaleDateString('tr-TR')}</strong></p>
            </div>

            <div style={{ margin: '16px 0' }}>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Hedef Tarih:</label>
              <DatePicker
                style={{ width: '100%' }}
                placeholder="İleri tarih seçin"
                disabledDate={(current) => current && current < dayjs().startOf('day')}
                onChange={(date) => setTransferDate(date ? date.toDate() : null)}
                format="DD.MM.YYYY"
              />
            </div>

            <div style={{ margin: '16px 0' }}>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Hedef Saat Dilimi:</label>
              <select
                className="form-select"
                value={transferStartTime && transferEndTime ? `${transferStartTime}-${transferEndTime}` : ''}
                onChange={(e) => {
                  if (e.target.value) {
                    const [s, en] = e.target.value.split('-');
                    setTransferStartTime(s);
                    setTransferEndTime(en);
                  } else {
                    setTransferStartTime('');
                    setTransferEndTime('');
                  }
                }}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db' }}
              >
                <option value="">Saat seçin...</option>
                {timeSlotOptions.map((opt) => (
                  <option key={opt.start} value={`${opt.start}-${opt.end}`}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ margin: '16px 0' }}>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Not (opsiyonel):</label>
              <textarea
                className="form-control"
                rows={2}
                placeholder="Aktarım sebebi..."
                value={transferNotes}
                onChange={(e) => setTransferNotes(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db', resize: 'vertical' }}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ClubPaymentPage;
